import React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import startCase from "lodash/startCase";
import Panel from "../../../../components/Panel";
import { Range, TestTypes } from "../../../../interfaces/Lab";
import { useAppSelector } from "../../../../store/hooks";
import { DiagnosticTypes, LabTestGroup, Test } from "../../../../interfaces/LabInterfaces";
import hasOwnProperty from "../../../../helpers/object";
import styles from "../../Lab.module.css";

const rangeFormatter = (range: Range): JSX.Element => {
  if (hasOwnProperty(range, "other")) {
    return (
      <>
        {range.other.label && <p>{startCase(range.other.label)}</p>}
        <span>{`Min: ${range.other.min || ""} Max: ${range.other.min || ""}`}</span>
      </>
    );
  }
  return (
    <div>
      {Object.keys(range || {}).map((item) => (
        <>
          {item && <p>{startCase(item)}</p>}
          <span>{`Min: ${range[item]?.min || ""} Max: ${range[item]?.max || ""}`}</span>
        </>
      ))}
    </div>
  );
};

const HeadRow = () => (
  <TableRow>
    <TableCell>Test name</TableCell>
    <TableCell>Unit</TableCell>
    <TableCell>Ranges</TableCell>
    <TableCell>Methods</TableCell>
    <TableCell>Default value/options</TableCell>
  </TableRow>
);

const Rows = ({
  row,
  subTestOffset = 2,
  isRadiology = false
}: {
  row: Test;
  subTestOffset?: number;
  isRadiology?: boolean;
}) => {
  const isRowRadiology =
    isRadiology ||
    (row as { type?: string })?.type === TestTypes.RADIOLOGY ||
    (row as { type?: string })?.type === DiagnosticTypes.RADIOLOGY ||
    (row as { type?: string })?.type === "radiology";

  return (
    <>
      <TableRow key={row.name} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
        <TableCell component="th" scope="row" sx={{ pl: subTestOffset }}>
          {row.name}
        </TableCell>
        <TableCell>{row.unit || ""}</TableCell>
        <TableCell>{rangeFormatter(row.ranges)}</TableCell>
        <TableCell>{row.methods || ""}</TableCell>
        <TableCell>
          {isRowRadiology && row.formData?.reading ? (
            <Box
              className={styles.additionalData}
              sx={{
                "& p": {
                  margin: "4px 0",
                  "&:first-of-type": { mt: 0 },
                  "&:last-of-type": { mb: 0 }
                }
              }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: row.formData.reading }}
            />
          ) : (
            row.formData?.reading || ""
          )}
        </TableCell>
      </TableRow>
      {row.subTests?.map((sub) => (
        <Rows
          row={sub}
          key={sub.id}
          subTestOffset={subTestOffset + 2}
          isRadiology={isRowRadiology}
        />
      ))}
    </>
  );
};

interface Props {
  onEdit: () => void;
  onClose: () => void;
}

const LabTestGroupDetailsPanel = ({ onClose, onEdit }: Props): JSX.Element => {
  const labGroup = useAppSelector((state) => state.labSettings.draftGroup as LabTestGroup);

  const isRadiologyGroup =
    labGroup?.type === DiagnosticTypes.RADIOLOGY ||
    labGroup?.type === (TestTypes.RADIOLOGY as unknown as DiagnosticTypes) ||
    (labGroup?.type as string) === "radiology";

  return (
    <Panel
      onClose={onClose}
      title={labGroup?.name || ""}
      wrapperStyle={{ minWidth: 900 }}
      editButton={
        <Button onClick={onEdit} variant="contained">
          Edit
        </Button>
      }
    >
      {labGroup && (
        <Box>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <HeadRow />
              </TableHead>
              <TableBody>
                {labGroup.labTests?.map((row) => (
                  <Rows row={row} key={row.id} isRadiology={isRadiologyGroup} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Panel>
  );
};

export default LabTestGroupDetailsPanel;
